import './styles.css';
import Matter from 'matter-js';

const { Engine, World, Bodies, Body, Composite, Events, Vector } = Matter;

type BallBody = Matter.Body & { plugin: { kind?: string; scored?: boolean } };
type P = { x: number; y: number; vx: number; vy: number; life: number; color: string; r: number };
type Pop = { x: number; y: number; text: string; life: number; color: string };

const app = document.querySelector<HTMLDivElement>('#app')!;
app.innerHTML = `<main class="shell">
  <section class="top"><div><b>Kopi Pinball</b><span>Matter.js collisions</span></div><button id="restart">Restart</button></section>
  <section class="stats"><div><span>Score</span><b id="score">0</b></div><div><span>Balls</span><b id="balls">3</b></div><div><span>Combo</span><b id="combo">0</b></div><div><span>Best</span><b id="best">0</b></div></section>
  <section class="goal"><b>Real collision playground</b><p>Launch kopi pearls into bumpers, snack sensors, and flippers. Tap sides for left/right flipper.</p></section>
  <canvas id="game" aria-label="Kopi Pinball Matter.js physics game"></canvas>
  <section class="bar"><button class="primary" id="launch">Launch pearl</button><p id="hint">Matter.js engine: rigid bodies, restitution, sensors, collisionStart events.</p></section>
</main>`;

const canvas = document.querySelector<HTMLCanvasElement>('#game')!;
const ctx = canvas.getContext('2d')!;
const $ = (s: string) => document.querySelector<HTMLElement>(s)!;

let W = 0, H = 0, DPR = 1;
let engine: Matter.Engine;
let ball: BallBody | null = null;
let bumpers: BallBody[] = [];
let snacks: BallBody[] = [];
let walls: Matter.Body[] = [];
let leftFlipper: BallBody;
let rightFlipper: BallBody;
let drain: BallBody;
let particles: P[] = [];
let pops: Pop[] = [];
let score = 0, best = Number(localStorage.getItem('kopi-pinball-best') || 0), balls = 3, combo = 0, comboClock = 0;
let last = 0, shake = 0, launched = false;
let leftDown = false, rightDown = false;

function fit() {
  DPR = Math.min(devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  W = rect.width; H = rect.height;
  canvas.width = Math.floor(W * DPR); canvas.height = Math.floor(H * DPR);
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
}
function rand(a: number, b: number) { return a + Math.random() * (b - a); }
function plugin<T extends Matter.Body>(b: T, kind: string) { (b as BallBody).plugin = { ...(b as BallBody).plugin, kind }; return b as BallBody; }
function pop(x: number, y: number, text: string, color = '#fff3bd') { pops.push({ x, y, text, color, life: 1 }); pops = pops.slice(-24); }
function puff(x: number, y: number, color: string, n = 14) { for (let i = 0; i < n; i++) { const a = rand(0, Math.PI * 2), v = rand(40, 230); particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rand(.28, .85), color, r: rand(2, 6) }); } }
function setText() { $('#score').textContent = String(score); $('#balls').textContent = String(balls); $('#combo').textContent = combo ? `x${combo}` : '—'; $('#best').textContent = String(best); $('#launch').textContent = ball && launched ? 'Nudge' : balls > 0 ? 'Launch pearl' : 'Again'; }

function makeWall(x: number, y: number, w: number, h: number, angle = 0) { const b = Bodies.rectangle(x, y, w, h, { isStatic: true, angle, restitution: .78, friction: .05 }); walls.push(b); return b; }
function makeBumper(x: number, y: number, r: number, colorKind: string) { const b = plugin(Bodies.circle(x, y, r, { isStatic: true, restitution: 1.65, friction: 0, label: 'bumper' }), 'bumper:' + colorKind); bumpers.push(b); return b; }
function makeSnack(x: number, y: number) { const b = plugin(Bodies.circle(x, y, 15, { isStatic: true, isSensor: true, label: 'snack' }), 'snack'); snacks.push(b); return b; }

function initWorld() {
  fit();
  engine = Engine.create({ gravity: { x: 0, y: 1.02 } });
  engine.constraintIterations = 4;
  engine.positionIterations = 8;
  walls = []; bumpers = []; snacks = []; particles = []; pops = [];
  ball = null; launched = false; score = 0; balls = 3; combo = 0; comboClock = 0; shake = 0;

  const bodies: Matter.Body[] = [];
  bodies.push(makeWall(W / 2, H + 20, W, 40));
  bodies.push(makeWall(-20, H / 2, 40, H));
  bodies.push(makeWall(W + 20, H / 2, 40, H));
  bodies.push(makeWall(W * .23, 110, W * .52, 18, .32));
  bodies.push(makeWall(W * .77, 110, W * .52, 18, -.32));
  bodies.push(makeWall(42, H * .52, 18, H * .62, -.14));
  bodies.push(makeWall(W - 42, H * .52, 18, H * .62, .14));
  bodies.push(makeWall(W * .31, H - 86, 128, 18, .22));
  bodies.push(makeWall(W * .69, H - 86, 128, 18, -.22));

  leftFlipper = plugin(Bodies.rectangle(W * .37, H - 70, 96, 16, { isStatic: true, restitution: 1.15, friction: .03, angle: .35, label: 'flipper' }), 'flipper:left');
  rightFlipper = plugin(Bodies.rectangle(W * .63, H - 70, 96, 16, { isStatic: true, restitution: 1.15, friction: .03, angle: -.35, label: 'flipper' }), 'flipper:right');
  bodies.push(leftFlipper, rightFlipper);
  drain = plugin(Bodies.rectangle(W / 2, H - 14, W * .28, 22, { isStatic: true, isSensor: true, label: 'drain' }), 'drain');
  bodies.push(drain);

  bodies.push(makeBumper(W * .5, H * .27, 34, 'gold'));
  bodies.push(makeBumper(W * .34, H * .39, 28, 'green'));
  bodies.push(makeBumper(W * .66, H * .39, 28, 'red'));
  bodies.push(makeBumper(W * .5, H * .52, 24, 'blue'));
  for (let i = 0; i < 8; i++) bodies.push(makeSnack(rand(84, W - 84), rand(155, H - 165)));

  World.add(engine.world, bodies);
  Events.on(engine, 'collisionStart', ev => {
    for (const pair of ev.pairs) {
      const a = pair.bodyA as BallBody, b = pair.bodyB as BallBody;
      const other = a === ball ? b : b === ball ? a : null;
      if (!ball || !other) continue;
      const kind = other.plugin?.kind || '';
      if (kind.startsWith('bumper')) {
        combo++; comboClock = 2.2;
        const gain = 35 + combo * 8;
        score += gain; shake = 7;
        const p = other.position;
        pop(p.x, p.y - 24, '+' + gain, kind.includes('gold') ? '#ffe48a' : '#9effbf');
        puff(p.x, p.y, kind.includes('red') ? '#ff7897' : kind.includes('green') ? '#7ee0a6' : '#f3c94c', 16);
        const away = Vector.normalise(Vector.sub(ball.position, other.position));
        Body.applyForce(ball, ball.position, Vector.mult(away, 0.035));
      } else if (kind === 'snack' && !other.plugin.scored) {
        other.plugin.scored = true; other.isSensor = true;
        combo++; comboClock = 2.5; score += 80 + combo * 10;
        pop(other.position.x, other.position.y, 'snack', '#ffe48a'); puff(other.position.x, other.position.y, '#ffe48a', 14);
        Composite.remove(engine.world, other); snacks = snacks.filter(s => s !== other);
        setTimeout(() => { if (engine) { const s = makeSnack(rand(84, W - 84), rand(155, H - 165)); World.add(engine.world, s); } }, 700);
      } else if (kind === 'drain') {
        loseBall();
      } else if (kind.startsWith('flipper')) {
        score += 3; puff(ball.position.x, ball.position.y, '#fff3bd', 4);
      }
    }
    best = Math.max(best, score); localStorage.setItem('kopi-pinball-best', String(best)); setText();
  });
  setText(); last = performance.now(); requestAnimationFrame(loop);
}

function newBall() {
  if (ball) Composite.remove(engine.world, ball);
  if (balls <= 0) { initWorld(); return; }
  balls--; launched = false;
  ball = plugin(Bodies.circle(W - 78, H - 86, 16, { restitution: .92, friction: .02, frictionAir: .004, density: .002, label: 'kopi-pearl' }), 'ball');
  World.add(engine.world, ball);
  pop(W - 78, H - 110, 'ready', '#fff3bd'); setText();
}
function launchOrNudge() {
  if (!ball || balls === 3 && !launched) newBall();
  if (!ball) return;
  if (!launched) { launched = true; Body.setVelocity(ball, { x: -8.5, y: -18.5 }); Body.applyForce(ball, ball.position, { x: -0.035, y: -0.08 }); puff(ball.position.x, ball.position.y, '#ffe48a', 18); pop(ball.position.x, ball.position.y - 22, 'launch', '#fff3bd'); }
  else { Body.applyForce(ball, ball.position, { x: ball.position.x < W / 2 ? .018 : -.018, y: -.014 }); shake = 4; }
  setText();
}
function loseBall() {
  if (!ball) return;
  pop(ball.position.x, H - 46, 'drain', '#ff9a9a'); puff(ball.position.x, H - 46, '#ff7897', 18);
  Composite.remove(engine.world, ball); ball = null; launched = false; combo = 0;
  if (balls <= 0) { pop(W / 2, H * .5, 'game over', '#ff9a9a'); }
  else setTimeout(newBall, 550);
  setText();
}
function setFlippers(dt: number) {
  const leftTarget = leftDown ? -.52 : .35;
  const rightTarget = rightDown ? .52 : -.35;
  Body.setAngle(leftFlipper, leftFlipper.angle + (leftTarget - leftFlipper.angle) * Math.min(1, dt * 18));
  Body.setAngle(rightFlipper, rightFlipper.angle + (rightTarget - rightFlipper.angle) * Math.min(1, dt * 18));
  if (ball && leftDown && ball.position.x < W * .52 && ball.position.y > H - 138) Body.applyForce(ball, ball.position, { x: .018, y: -.045 });
  if (ball && rightDown && ball.position.x > W * .48 && ball.position.y > H - 138) Body.applyForce(ball, ball.position, { x: -.018, y: -.045 });
}
function update(dt: number) {
  setFlippers(dt);
  Engine.update(engine, dt * 1000);
  if (comboClock > 0) comboClock -= dt; else combo = 0;
  particles = particles.map(p => ({ ...p, x: p.x + p.vx * dt, y: p.y + p.vy * dt, vy: p.vy + 140 * dt, life: p.life - dt })).filter(p => p.life > 0);
  pops = pops.map(p => ({ ...p, y: p.y - 28 * dt, life: p.life - 1.25 * dt })).filter(p => p.life > 0);
  if (ball && ball.position.y > H + 80) loseBall();
  setText();
}

function drawBody(b: Matter.Body, fill: string, stroke = 'rgba(255,255,255,.45)') {
  ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
  ctx.fillStyle = fill; ctx.strokeStyle = stroke; ctx.lineWidth = 2;
  const verts = b.vertices.map(v => ({ x: v.x - b.position.x, y: v.y - b.position.y }));
  ctx.beginPath(); verts.forEach((v, i) => i ? ctx.lineTo(v.x, v.y) : ctx.moveTo(v.x, v.y)); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore();
}
function drawCircle(b: Matter.Body, r: number, fill: string) { ctx.save(); ctx.shadowColor = fill; ctx.shadowBlur = 18; ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(b.position.x, b.position.y, r, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = 'rgba(255,255,255,.72)'; ctx.beginPath(); ctx.arc(b.position.x - r * .32, b.position.y - r * .35, r * .22, 0, Math.PI * 2); ctx.fill(); ctx.restore(); }
function draw() {
  fit(); ctx.save(); if (shake > 0) { ctx.translate(rand(-shake, shake), rand(-shake, shake)); shake *= .82; }
  const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#28160d'); bg.addColorStop(.52, '#4b2511'); bg.addColorStop(1, '#120b07'); ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(255,255,255,.06)'; for (let i = 0; i < 18; i++) ctx.fillRect((i * 94 + 20) % W, 80 + (i % 5) * 48, 58, 12);
  for (const w of walls) drawBody(w, 'rgba(255,228,138,.22)', 'rgba(255,228,138,.38)');
  drawBody(leftFlipper, '#7ee0a6'); drawBody(rightFlipper, '#7ee0a6'); drawBody(drain, 'rgba(255,80,110,.28)', 'rgba(255,120,151,.5)');
  for (const b of bumpers) drawCircle(b, (b.circleRadius || 26), b.plugin.kind?.includes('red') ? '#ff7897' : b.plugin.kind?.includes('green') ? '#7ee0a6' : b.plugin.kind?.includes('blue') ? '#80d7ff' : '#f3c94c');
  for (const s of snacks) drawCircle(s, 15, '#ffe48a');
  if (ball) { drawCircle(ball, 16, '#5a2a16'); ctx.fillStyle = '#fff3bd'; ctx.beginPath(); ctx.arc(ball.position.x - 5, ball.position.y - 5, 4, 0, Math.PI * 2); ctx.arc(ball.position.x + 6, ball.position.y - 5, 4, 0, Math.PI * 2); ctx.fill(); }
  for (const p of particles) { ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); } ctx.globalAlpha = 1;
  for (const p of pops) { ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color; ctx.font = '900 15px Inter, system-ui'; ctx.textAlign = 'center'; ctx.fillText(p.text, p.x, p.y); } ctx.globalAlpha = 1;
  if (!ball && balls <= 0) { ctx.fillStyle = 'rgba(0,0,0,.44)'; ctx.fillRect(0, 0, W, H); ctx.fillStyle = '#fff5da'; ctx.font = '900 36px Inter'; ctx.textAlign = 'center'; ctx.fillText('Run ended', W / 2, H / 2); ctx.font = '800 17px Inter'; ctx.fillText('Tap launch for another run', W / 2, H / 2 + 34); }
  ctx.restore();
}
function loop(t: number) { const dt = Math.min(.033, (t - last) / 1000); last = t; update(dt); draw(); requestAnimationFrame(loop); }

function pointerSide(e: PointerEvent) { const r = canvas.getBoundingClientRect(); return e.clientX - r.left < r.width / 2 ? 'left' : 'right'; }
canvas.addEventListener('pointerdown', e => { const side = pointerSide(e); if (side === 'left') leftDown = true; else rightDown = true; if (!ball || !launched) launchOrNudge(); });
canvas.addEventListener('pointerup', () => { leftDown = false; rightDown = false; });
canvas.addEventListener('pointercancel', () => { leftDown = false; rightDown = false; });
$('#launch').onclick = launchOrNudge;
$('#restart').onclick = initWorld;
addEventListener('keydown', e => { if (e.key === 'ArrowLeft' || e.key === 'a') leftDown = true; if (e.key === 'ArrowRight' || e.key === 'd') rightDown = true; if (e.key === ' ') launchOrNudge(); });
addEventListener('keyup', e => { if (e.key === 'ArrowLeft' || e.key === 'a') leftDown = false; if (e.key === 'ArrowRight' || e.key === 'd') rightDown = false; });
addEventListener('resize', () => { initWorld(); });
fit(); initWorld(); newBall();
